<template>
  <div class="pa-3">

    <!-- ── Toolbar ─────────────────────────────────────────────────────── -->
    <v-row align="center" class="mb-1" dense>
      <v-col cols="auto">
        <h2 class="text-h6 font-weight-bold">Booking Translations</h2>
        <div class="caption grey--text">
          Drag headers to reorder columns · Edit inline · Auto-saves on blur
        </div>
      </v-col>
      <v-spacer />
      <v-col cols="auto">
        <v-text-field
          v-model="search"
          dense hide-details outlined clearable
          prepend-inner-icon="mdi-magnify"
          placeholder="Filter keys…"
          style="width:220px"
        />
      </v-col>
      <v-col cols="auto">
        <v-btn icon :loading="loading" title="Reload all" @click="loadAll">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-col>
    </v-row>

    <!-- ── Legend ─────────────────────────────────────────────────────── -->
    <div class="caption mb-2">
      <span class="leg ok">■</span> Translated &nbsp;
      <span class="leg untranslated">■</span> Same as English (needs review) &nbsp;
      <span class="leg missing">■</span> Missing
    </div>

    <!-- ── Table ──────────────────────────────────────────────────────── -->
    <div class="table-wrap">
      <table class="trans-table">
        <thead>
          <tr>
            <!-- Sticky key column — not draggable -->
            <th class="sticky-col th-key">
              English key
            </th>

            <!-- Draggable language columns -->
            <th
              v-for="(lang, idx) in langColumns"
              :key="lang"
              class="th-lang"
              :class="{ 'drag-over': dragTarget === idx && dragFrom !== idx }"
              draggable="true"
              @dragstart="onDragStart(idx, $event)"
              @dragover.prevent="dragTarget = idx"
              @dragleave="dragTarget = null"
              @drop.prevent="dropColumn(idx)"
              @dragend="dragTarget = null; dragFrom = null"
            >
              <div class="d-flex align-center justify-space-between">
                <span>
                  <v-icon x-small class="grey--text" style="cursor:grab">mdi-drag-vertical</v-icon>
                  {{ langMeta[lang].flag }} {{ langMeta[lang].name }}
                </span>
                <v-btn
                  x-small text :loading="translating === lang"
                  :title="`Auto-translate missing ${langMeta[lang].name} keys via Azure`"
                  @click.stop="autoTranslate(lang)"
                >
                  <v-icon x-small>mdi-translate</v-icon>
                </v-btn>
              </div>
            </th>
          </tr>
        </thead>

        <tbody>
          <tr v-if="loading">
            <td :colspan="langColumns.length + 1" class="text-center pa-4 grey--text">
              <v-progress-circular indeterminate size="24" color="teal" class="mr-2" />
              Loading…
            </td>
          </tr>

          <tr v-for="key in filteredKeys" :key="key">
            <!-- Key cell (sticky) -->
            <td class="sticky-col td-key">
              <div class="key-text" :title="key">{{ key }}</div>
            </td>

            <!-- Translation cells -->
            <td
              v-for="lang in langColumns"
              :key="lang"
              class="td-lang"
              :class="cellStatus(lang, key)"
            >
              <div class="cell-wrap">
                <textarea
                  class="cell-input"
                  rows="2"
                  :value="translations[lang] && translations[lang][key]"
                  @input="onInput(lang, key, $event.target.value)"
                  @blur="saveKey(lang, key)"
                />
                <v-progress-circular
                  v-if="saving[lang + ':' + key]"
                  indeterminate size="10" width="2" color="teal"
                  class="save-spin"
                />
              </div>
            </td>
          </tr>

          <tr v-if="!loading && filteredKeys.length === 0">
            <td :colspan="langColumns.length + 1" class="text-center pa-4 grey--text">
              No keys match "{{ search }}"
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- ── Snackbar ────────────────────────────────────────────────────── -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="2000" bottom>
      {{ snackbar.text }}
    </v-snackbar>

  </div>
</template>

<script>
export default {
  name: 'BookingTranslations',

  data() {
    return {
      enKeys: {},
      translations: {},   // { fr: { key: value, … }, … }

      langColumns: ['it', 'fr', 'de', 'es', 'pt', 'no', 'jp'],
      langMeta: {
        it: { name: 'Italiano',  flag: '🇮🇹' },
        fr: { name: 'Français',  flag: '🇫🇷' },
        de: { name: 'Deutsch',   flag: '🇩🇪' },
        es: { name: 'Español',   flag: '🇪🇸' },
        pt: { name: 'Português', flag: '🇧🇷' },
        no: { name: 'Norsk',     flag: '🇳🇴' },
        jp: { name: '日本語',    flag: '🇯🇵' },
      },

      search: '',
      loading: false,
      saving: {},       // { 'fr:When': true }
      translating: null,

      dragFrom: null,
      dragTarget: null,

      snackbar: { show: false, text: '', color: 'success' },
    };
  },

  computed: {
    filteredKeys() {
      const s = (this.search || '').toLowerCase();
      return Object.keys(this.enKeys).filter(k =>
        !s || k.toLowerCase().includes(s) || (this.enKeys[k] || '').toLowerCase().includes(s)
      );
    },
  },

  created() {
    this.loadAll();
  },

  methods: {
    // ── Data loading ─────────────────────────────────────────────────────
    async loadAll() {
      this.loading = true;
      try {
        // EN fetched directly (source of truth, no caching concern)
        // Lang files routed through PHP: always fresh, keys reconciled with EN
        const enRes  = await fetch('/static/i18n/en/Booking.json').then(r => r.ok ? r.json() : {});
        this.enKeys  = enRes;

        await Promise.all(this.langColumns.map(async lang => {
          const data = await fetch(
            `/ajax/API/microsoft/translate.php?action=read&lang=${lang}&page=Booking`
          ).then(r => r.ok ? r.json() : {}).catch(() => ({}));

          // All EN keys are guaranteed present from PHP; pre-fill missing with ""
          const full = {};
          Object.keys(enRes).forEach(k => { full[k] = data[k] ?? ''; });
          this.$set(this.translations, lang, full);
        }));
      } catch {
        this.showSnack('Load failed', 'error');
      } finally {
        this.loading = false;
      }
    },

    // ── Editing ───────────────────────────────────────────────────────────
    onInput(lang, key, value) {
      if (this.translations[lang]) {
        this.$set(this.translations[lang], key, value);
      }
    },

    async saveKey(lang, key) {
      const value = (this.translations[lang] && this.translations[lang][key]) || '';
      const sk = `${lang}:${key}`;
      this.$set(this.saving, sk, true);
      try {
        const res = await fetch('/ajax/API/microsoft/translate.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ lang, page: 'Booking', key, value }),
        });
        const text = await res.text();
        if (!res.ok) {
          console.error('translate.php save error:', res.status, text);
          this.showSnack('Save failed: ' + text.slice(0, 120), 'error');
        }
        // success: no snackbar noise on every keystroke blur
      } catch (e) {
        console.error('translate.php fetch error:', e);
        this.showSnack('Network error saving translation', 'error');
      } finally {
        this.$delete(this.saving, sk);
      }
    },

    // ── Auto-translate via Azure ──────────────────────────────────────────
    async autoTranslate(lang) {
      this.translating = lang;
      try {
        const res = await fetch(
          `/ajax/API/microsoft/translate.php?lang=${encodeURIComponent(lang)}&page=Booking`
        );
        if (!res.ok) throw new Error();
        const data = await res.json();
        // Rebuild full map so all EN keys are present
        const full = {};
        Object.keys(this.enKeys).forEach(k => { full[k] = data[k] ?? ''; });
        this.$set(this.translations, lang, full);
        this.showSnack(`${this.langMeta[lang].name} auto-translated`, 'success');
      } catch {
        this.showSnack('Auto-translate failed', 'error');
      } finally {
        this.translating = null;
      }
    },

    // ── Column drag & drop ────────────────────────────────────────────────
    onDragStart(idx, evt) {
      this.dragFrom = idx;
      evt.dataTransfer.effectAllowed = 'move';
    },

    dropColumn(toIdx) {
      const from = this.dragFrom;
      if (from === null || from === toIdx) return;
      const cols = [...this.langColumns];
      const [moved] = cols.splice(from, 1);
      cols.splice(toIdx, 0, moved);
      this.langColumns = cols;
    },

    // ── Cell status ───────────────────────────────────────────────────────
    cellStatus(lang, key) {
      const val = this.translations[lang] && this.translations[lang][key];
      if (!val) return 'missing';
      if (val === this.enKeys[key]) return 'untranslated';
      return 'ok';
    },

    showSnack(text, color = 'success') {
      this.snackbar = { show: true, text, color };
    },
  },
};
</script>

<style scoped>
/* ── Layout ─────────────────────────────────────────────────────────────── */
.table-wrap {
  overflow-x: auto;
  overflow-y: auto;
  max-height: calc(100vh - 160px);
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}

/* ── Table base ─────────────────────────────────────────────────────────── */
.trans-table {
  border-collapse: collapse;
  min-width: 100%;
  font-size: 13px;
  table-layout: fixed;
}
.trans-table th,
.trans-table td {
  border: 1px solid #e0e0e0;
  padding: 4px 8px;
  vertical-align: top;
}

/* ── Header row ─────────────────────────────────────────────────────────── */
.trans-table thead th {
  position: sticky;
  top: 0;
  background: #f5f5f5;
  z-index: 3;
  white-space: nowrap;
  user-select: none;
}
.th-key {
  width: 240px;
}
.th-lang {
  width: 210px;
  cursor: grab;
}
.th-lang:active {
  cursor: grabbing;
}
.drag-over {
  outline: 2px solid #1976d2;
  outline-offset: -2px;
}

/* ── Sticky first column ────────────────────────────────────────────────── */
.sticky-col {
  position: sticky;
  left: 0;
  z-index: 2;
  background: white;
}
thead .sticky-col {
  z-index: 4;
  background: #f5f5f5;
}

/* ── Key cell ───────────────────────────────────────────────────────────── */
.td-key {
  width: 240px;
}
.key-text {
  font-size: 12px;
  font-weight: 600;
  white-space: normal;
  word-break: break-word;
  line-height: 1.4;
}

/* ── Translation cell ───────────────────────────────────────────────────── */
.td-lang {
  width: 210px;
  padding: 3px 6px;
  position: relative;
}
td.ok          { background: #f1fff5; }
td.untranslated { background: #fff8e1; }
td.missing     { background: #fff0f0; }

.cell-wrap {
  position: relative;
  display: flex;
  align-items: flex-start;
}
.cell-input {
  width: 100%;
  resize: vertical;
  border: none;
  outline: none;
  font-size: 12px;
  line-height: 1.4;
  background: transparent;
  font-family: inherit;
  padding: 2px 14px 2px 0;
}
.cell-input:focus {
  background: #fffde7;
  border-radius: 2px;
}
.save-spin {
  position: absolute;
  top: 3px;
  right: 2px;
}

/* ── Legend ─────────────────────────────────────────────────────────────── */
.leg { font-size: 13px; }
.leg.ok          { color: #43a047; }
.leg.untranslated { color: #fb8c00; }
.leg.missing     { color: #e53935; }
</style>
